/**
 * Hook för SimCardManagement
 * Hanterar state och logik för SIM-korthantering via Jasper API
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import i18n from '@/i18n/config';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { useLanguage } from '@/store/languageStore';
import { uiDateLocale } from '@/utils/uiLocale';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import {
  SimCard,
  AggregatedUsage,
  RatePlan,
  SimTab,
  SIM_STATUS_CONFIG,
} from '@/types/simCard';

const PAGE_SIZE = 50;

export function useSimCardManagement() {
  const { language } = useLanguage();

  // Data state
  const [simCards, setSimCards] = useState<SimCard[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [aggregatedUsage, setAggregatedUsage] = useState<AggregatedUsage | null>(null);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<SimTab>('overview');

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const seenOverview = useRef(false);
  const usagePolls = useRef(0);

  // Load data on mount
  useEffect(() => {
    loadAggregatedUsage();
    loadRatePlans();
  }, []);

  // Load SIM cards when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'simcards') {
      loadSimCards();
    }
  }, [activeTab, statusFilter, page]);

  const loadAggregatedUsage = useCallback(async (refresh = false) => {
    if (refresh) usagePolls.current = 0;
    if (refresh || !seenOverview.current) setLoading(true);
    try {
      const response = await api.get('/api/sim/usage/aggregated', {
        params: refresh ? { refresh: 1 } : undefined,
      });
      if (response.data.success) {
        const data = response.data.data as AggregatedUsage;
        setAggregatedUsage(data);
        seenOverview.current = true;
        if (data.usagePending && usagePolls.current < 45) {
          usagePolls.current += 1;
          window.setTimeout(() => {
            void loadAggregatedUsage(false);
          }, 4000);
        } else if (!data.usagePending) {
          usagePolls.current = 0;
        }
      }
    } catch {
      toast.error(i18n.t('copy.hooks.simUsageFetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [language]);

  const loadRatePlans = useCallback(async () => {
    try {
      const response = await api.get('/api/sim/rate-plans');
      if (response.data.success) {
        setRatePlans(response.data.data);
      }
    } catch {
      // Silent fail - not critical
    }
  }, []);

  const loadSimCards = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', page.toString());
      params.append('pageSize', PAGE_SIZE.toString());

      const response = await api.get(`/api/sim/cards?${params.toString()}`);
      if (response.data.success) {
        setSimCards(response.data.data.simCards);
        setTotalCount(response.data.data.totalCount);
      }
    } catch {
      toast.error(i18n.t('copy.hooks.simCardsFetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, language]);

  const createReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/sim/reports', {
        reportType: 'AGGREGATED_USAGE',
      });

      if (response.data.success) {
        toast.success(i18n.t('copy.hooks.reportCreated'));
        if (response.data.data.localReport && response.data.data.usage) {
          setAggregatedUsage({
            totalDevices: response.data.data.usage.totalDevices || 0,
            totalDataUsageMB: Math.round((response.data.data.usage.totalDataUsage || 0) / 1024 / 1024 * 100) / 100,
            totalDataUsageGB: Math.round((response.data.data.usage.totalDataUsage || 0) / 1024 / 1024 / 1024 * 100) / 100,
            totalSmsUsage: response.data.data.usage.totalSmsUsage || 0,
            totalVoiceUsage: response.data.data.usage.totalVoiceUsage || 0,
            billingCycle: response.data.data.usage.billingCycle || '',
          });
        }
      }
    } catch {
      toast.error(i18n.t('copy.hooks.reportCreateFailed'));
    } finally {
      setLoading(false);
    }
  }, [language]);

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const dateLocale = uiDateLocale(language);
      return date.toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }, [language]);

  const getStatusConfig = useCallback((status?: string) => {
    return SIM_STATUS_CONFIG[status || ''] || {
      label: status || '-',
      labelEn: status || '-',
      color: 'text-textSecondary',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
      icon: DevicePhoneMobileIcon,
    };
  }, []);

  // Filter SIM cards locally by search term
  const filteredSimCards = useMemo(() => {
    return simCards.filter(sim => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        sim.iccid?.toLowerCase().includes(term) ||
        sim.msisdn?.toLowerCase().includes(term) ||
        sim.imei?.toLowerCase().includes(term) ||
        sim.ratePlan?.toLowerCase().includes(term)
      );
    });
  }, [simCards, searchTerm]);

  const handleStatusFilterChange = useCallback((status: string) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setPage(p => Math.max(1, p - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(p => p + 1);
  }, []);

  return {
    // Data
    simCards: filteredSimCards,
    totalCount,
    aggregatedUsage,
    ratePlans,

    // UI State
    loading,
    activeTab,

    // Filters
    statusFilter,
    searchTerm,
    page,
    pageSize: PAGE_SIZE,

    // Setters
    setActiveTab,
    setSearchTerm,

    // Actions
    loadAggregatedUsage,
    loadSimCards,
    createReport,
    formatDate,
    getStatusConfig,
    handleStatusFilterChange,
    goToPreviousPage,
    goToNextPage,
  };
}
